import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, of } from 'rxjs';

import { environment } from 'src/environments/environment';
import { CreateOrderRequest, CreateOrderResponse, Order, OrderListResponse, OrderStatus } from '../interfaces/order';

@Injectable({
  providedIn: 'root'
})
export class OrderApiService {
  private readonly API_URL = `${environment.apiUrl}/orders`;

  constructor(private http: HttpClient) { }

  /**
   * Create a new order (or multiple sub-orders)
   */
  createOrder(request: CreateOrderRequest): Observable<CreateOrderResponse> {
    return this.http.post<CreateOrderResponse>(`${this.API_URL}/create`, request).pipe(
      map(response => ({
        ...response,
        orders: response.orders.map(order => this.mapOrder(order))
      })),
      catchError(error => {
        console.error('Error creating order:', error);
        throw error;
      })
    );
  }

  /**
   * Get all orders for the current user
   */
  getMyOrders(page: number = 1, size: number = 20, status?: OrderStatus): Observable<OrderListResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (status) {
      params = params.set('status', status);
    }

    return this.http.get<OrderListResponse>(`${this.API_URL}/my-orders`, { params }).pipe(
      map(response => ({
        ...response,
        orders: response.orders.map(order => this.mapOrder(order))
      })),
      catchError(error => {
        console.error('Error fetching orders:', error);
        return of({ orders: [], total: 0, page: 1, pages: 0, hasMore: false });
      })
    );
  }

  /**
   * Get a single order by ID
   */
  getOrder(orderId: number): Observable<Order | undefined> {
    return this.http.get<Order>(`${this.API_URL}/${orderId}`).pipe(
      map(order => this.mapOrder(order)),
      catchError(error => {
        console.error(`Error fetching order ${orderId}:`, error);
        return of(undefined);
      })
    );
  }

  /**
   * Cancel an order
   */
  cancelOrder(orderId: number): Observable<Order | undefined> {
    return this.http.post<Order>(`${this.API_URL}/${orderId}/cancel`, {}).pipe(
      map(order => this.mapOrder(order)),
      catchError(error => {
        console.error(`Error cancelling order ${orderId}:`, error);
        throw error;
      })
    );
  }

  /**
   * Create a return request for an order
   */
  createReturnRequest(orderId: number, returnData: any): Observable<any> {
    return this.http.post(`${this.API_URL}/${orderId}/return`, returnData).pipe(
      catchError(error => {
        console.error(`Error creating return request for order ${orderId}:`, error);
        throw error;
      })
    );
  }

  /**
   * Get return request for an order
   */
  getReturnRequest(orderId: number): Observable<any> {
    return this.http.get(`${this.API_URL}/${orderId}/return`).pipe(
      catchError(error => {
        console.error(`Error fetching return request for order ${orderId}:`, error);
        return of(null);
      })
    );
  }

  /**
   * Add a message to a return request
   */
  addReturnMessage(returnId: number, body: { message: string }): Observable<any> {
    return this.http.post(`${environment.apiUrl}/orders/return/${returnId}/message`, body).pipe(
      catchError(error => {
        console.error(`Error adding return message for return ${returnId}:`, error);
        throw error;
      })
    );
  }

  /**
   * Map API response to frontend Order interface
   */
  private mapOrder(order: any): Order {
    return {
      ...order,
      createdAt: order.createdAt ? new Date(order.createdAt) : new Date(),
      updatedAt: order.updatedAt ? new Date(order.updatedAt) : undefined,
      completedAt: order.completedAt ? new Date(order.completedAt) : undefined,
      pickupTime: order.pickupTime ? new Date(order.pickupTime) : undefined,
      deliveryTime: order.deliveryTime ? new Date(order.deliveryTime) : undefined,
    };
  }
  downloadInvoice(orderId: number): Observable<Blob> {
    return this.http.get(`${this.API_URL}/hub/${orderId}/invoice/download`, {
      responseType: 'blob'
    });
  }
}